import { useCallback, useEffect, useRef, useState } from "react";
import ComprasService from "../api/compras.api";

/**
 * Hook que carga los pedidos de compra pendientes del ejercicio (GET /compras/:ano/estados).
 * Con intervaloMs > 0 los refresca periódicamente y en silencio (sin loading).
 * @param {number} ejercicio - Año del ejercicio
 * @param {{ intervaloMs?: number }} [opciones] - Refresco periódico opcional
 * @returns {{ pedidos: Array, loading: boolean, error: (string|null), recargar: Function }}
 */
const usePedidosCompra = (ejercicio, { intervaloMs } = {}) => {
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Se incrementa al cambiar de ejercicio o desmontar: invalida respuestas en vuelo
    const versionRef = useRef(0);
    const enVueloRef = useRef(false);

    const cargar = useCallback(async (silencioso) => {
        const version = versionRef.current;
        enVueloRef.current = true;
        if (!silencioso) setLoading(true);
        try {
            const datos = await ComprasService.getEstados(ejercicio);
            if (version !== versionRef.current) return;
            setPedidos(datos);
            setError(null);
        } catch (e) {
            if (version !== versionRef.current) return;
            setError(e.message);
        }
        enVueloRef.current = false;
        setLoading(false);
    }, [ejercicio]);

    const recargar = useCallback(() => cargar(false), [cargar]);

    useEffect(() => {
        const version = ++versionRef.current;
        enVueloRef.current = false;
        cargar(false);
        const temporizador = intervaloMs > 0
            ? setInterval(() => {
                if (!enVueloRef.current) cargar(true);
            }, intervaloMs)
            : null;
        return () => {
            if (temporizador) clearInterval(temporizador);
            if (versionRef.current === version) versionRef.current++;
        };
    }, [cargar, intervaloMs]);

    return { pedidos, loading, error, recargar };
};

export default usePedidosCompra;
